/* eslint-disable @angular-eslint/no-output-native */
import { KeyValuePipe, NgTemplateOutlet } from '@angular/common';
import type { OnInit, TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { ControlValueAccessor } from '@angular/forms';
import { FormsModule, NgControl } from '@angular/forms';
import {
  NgClearButtonTemplateDirective,
  NgLabelTemplateDirective,
  NgOptionTemplateDirective,
  NgSelectComponent,
} from '@ng-select/ng-select';

import type { Maybe, ValueOf } from '../../types/base';
import { uniqueId } from '../../utils/operators';
import { ButtonComponent } from '../button';
import { IconComponent } from '../icon/icon.component';

export type SelectValue = string | number | boolean | null;

export type SelectOption<T extends SelectValue = SelectValue> = {
  value: T;
  label?: string;
};

export const selectSizes = {
  /** default value */
  MEDIUM: 'md',
  LARGE: 'lg',
} as const;
export type SelectSize = ValueOf<typeof selectSizes>;

export type SelectTemplateContext = {
  $implicit: SelectOption;
};

@Component({
  selector: 'evc-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  imports: [
    FormsModule,
    NgClearButtonTemplateDirective,
    NgSelectComponent,
    NgOptionTemplateDirective,
    NgLabelTemplateDirective,
    NgTemplateOutlet,
    KeyValuePipe,
    IconComponent,
    ButtonComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[attr.data-size]': 'size()',
  },
})
export class SelectComponent implements ControlValueAccessor, OnInit {
  size = input<SelectSize>(selectSizes.MEDIUM);
  label = input<string>();
  value = input<SelectValue>('');
  name = input<string>();
  id = input<string>();
  placeholder = input<string>();
  disabled = input<boolean>(false);
  required = input<boolean>(false);
  readOnly = input<boolean>(false);
  clearable = input<boolean>(false);
  search = input<boolean>(false);
  loading = input<boolean | number>(false);
  showError = input<boolean>(true);
  errorText = input<string>('');
  options = input<Maybe<SelectOption[]>>(undefined);
  nullable = input<boolean>(false);
  optionTemplate = input<TemplateRef<SelectTemplateContext>>();
  labelTemplate = input<TemplateRef<SelectTemplateContext>>();

  readonly focus = output<FocusEvent>();
  readonly blur = output<FocusEvent>();
  readonly change = output<SelectValue>();
  readonly clear = output();

  private readonly select = viewChild(NgSelectComponent);

  get nativeElement(): HTMLInputElement | undefined {
    return this.select()?.searchInput()?.nativeElement;
  }

  readonly #controlDir = inject(NgControl, { optional: true, self: true });
  readonly #changeDetectorRef = inject(ChangeDetectorRef);
  readonly #destroyRef = inject(DestroyRef);
  readonly #formDisabled = signal(false);
  readonly #generatedId = uniqueId('select');
  #onChange: (value: SelectValue) => void = () => undefined;
  #onTouched: () => void = () => undefined;

  protected readonly localValue = linkedSignal(() => this.value());
  protected readonly selectValue = computed(() =>
    this.localValue() === '' ? null : this.localValue(),
  );
  protected readonly isDisabled = computed(() => this.disabled() || this.#formDisabled());
  protected readonly safeId = computed(() => this.id() ?? this.#generatedId);
  protected readonly controlDir = this.#controlDir;

  constructor() {
    if (this.#controlDir) {
      this.#controlDir.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.#controlDir?.statusChanges
      ?.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.#changeDetectorRef.markForCheck());
  }

  writeValue(value: SelectValue): void {
    this.localValue.set(value);
    this.#changeDetectorRef.markForCheck();
  }

  registerOnChange(fn: (value: SelectValue) => void): void {
    this.#onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.#onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.#formDisabled.set(disabled);
  }

  protected onInputChanged(value: SelectValue): void {
    this.localValue.set(value);
    this.#onChange(value);
    this.change.emit(value);
  }

  protected onFocus(event: FocusEvent): void {
    this.focus.emit(event);
  }

  protected onBlur(event: FocusEvent): void {
    this.#onTouched();
    this.blur.emit(event);
  }

  protected hasErrors(): boolean {
    return this.showError() && (!!this.#controlDir?.invalid || this.errorText() !== '');
  }
}
